import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useNavigateCarrying } from "@/lib/carryParams";
import { ArrowRight, Battery, Bell, Building2, Cog, Search, User } from "lucide-react";
import { TOPOLOGIES } from "@/data/topologies";
import { useAdapter, useAvailableSites, useSiteStore, useTopology } from "@/store/site";
import { useAlarms } from "@/hooks/useAlarms";
import { pushToast } from "@/hooks/useToasts";
import { useUiPrefsStore, HOME_VIEW_PREF } from "@/store/uiPrefs";
import { isValidHomeView } from "@/lib/routes";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";
import { NAV_CHORD, NAV_LABEL_KEY, NAV_TREE } from "@/lib/navTree";
import { rememberedTabLocation } from "@/lib/tabMemory";
import type { ControllerMode } from "@/data/types";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Minimal Tab-key focus trap for the hand-rolled modals (XTRA-04) that are
 * too entangled for a Radix Dialog rewrite — the palette, schedule
 * EntryEditor and OverridePanel. On Tab past the last focusable element it
 * wraps to the first (and vice versa for Shift+Tab), so keyboard focus can
 * never escape into the obscured page behind the dialog. Attach to the
 * dialog container's onKeyDown.
 */
export function trapTabKey(e: React.KeyboardEvent, container: HTMLElement | null) {
  if (e.key !== "Tab" || !container) return;
  const nodes = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((el) => el.offsetParent !== null); // skip display:none subtrees
  if (nodes.length === 0) {
    e.preventDefault();
    return;
  }
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  // idx === -1 covers focus sitting on the container itself (tabIndex={-1}
  // initial focus) or anywhere outside the list — wrap instead of escaping.
  const idx = nodes.indexOf(document.activeElement as HTMLElement);
  if (e.shiftKey) {
    if (idx <= 0) {
      e.preventDefault();
      last.focus();
    }
  } else if (idx === -1 || idx === nodes.length - 1) {
    e.preventDefault();
    first.focus();
  }
}

type CmdGroup = "Go" | "Site" | "Alarm" | "Mode" | "Action";

// Fixed render weight so navigation rows always sit above the write/action
// lanes — write commands (Mode overrides, Actions) can never be interleaved
// above a "Go" row regardless of Map-insertion order (Part 3, NAV-IA).
const GROUP_ORDER: Record<CmdGroup, number> = {
  Go: 0,
  Alarm: 1,
  Site: 2,
  Mode: 3,
  Action: 4,
};

interface Cmd {
  id: string;
  label: string;
  hint?: string;
  group: CmdGroup;
  icon: any;
  /**
   * Hidden, English search index (Risk 4): once labels are translated the
   * filter must still match English muscle-memory ("historical") and the
   * bare chord letter in any locale. Not shown in the UI.
   */
  keywords?: string;
  /**
   * Two-step write safety (XTRA-01): when set, the first Enter/click ARMS
   * the command and shows this text inline; only a second consecutive
   * Enter/click runs it. Any cursor move, query change or close disarms.
   */
  confirm?: string;
  /**
   * "Changes the live plant" marker (N13/N15). Drives the always-visible
   * badge + row tint INDEPENDENTLY of `hint`, so a row can show a real
   * hint string AND the badge:
   *   - 'destructive' — worst write (OFF): critical/red badge + red row text.
   *   - 'write'       — operational plant/state write (the other overrides,
   *     ack-all, clear-override): calmer amber/warn badge, row text left
   *     uncoloured so OFF still reads worst.
   * Preference writes (set/clear home view, set operator) DON'T touch the
   * plant and carry NO writeRisk — the badge stays "touches the live plant".
   */
  writeRisk?: "write" | "destructive";
  run: () => void;
}

/**
 * The single keyboard surface for power users. ⌘K opens it; arrow keys move
 * the cursor; Enter runs the selected command. Searches the union of:
 *   - Page navigations (the sidebar, again, but reachable without the mouse)
 *   - Site swaps (across topologies)
 *   - Mode overrides (with safety: 30-min auto-expire)
 *   - Bulk actions (acknowledge all warnings, etc.)
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Carrying navigate (lib/carryParams): palette hops keep the same time
  // window + session params a tab click would keep, and use replace
  // history inside the TB iframe.
  const navigate = useNavigateCarrying();
  const t = useT();
  const adapter = useAdapter();
  const topology = useTopology();
  const setSite = useSiteStore((s) => s.setSite);
  const setOperator = useSiteStore((s) => s.setOperator);
  const operator = useSiteStore((s) => s.operator);
  const mode = useSiteStore((s) => s.mode);
  const availableSites = useAvailableSites();
  // Shared alarms cache — same react-query key every other consumer mounts,
  // so indexing active alarms here adds no extra fetch (see useAlarms.ts).
  const { data: alarms } = useAlarms();
  // Current route (to save as the home view) + the per-site home.view pref
  // setter/value (F23). Reading the raw store value keeps the palette in
  // step with RootRedirect's readUiPref read.
  const { pathname } = useLocation();
  const setPref = useUiPrefsStore((s) => s.setPref);
  const currentHome = useUiPrefsStore((s) => s.prefs[topology.id]?.[HOME_VIEW_PREF] as string | undefined);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [armedId, setArmedId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setArmedId(null);
    if (open) {
      setQuery("");
      setCursor(0);
      const t = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    function onEsc() { onClose(); }
    window.addEventListener("ems:escape", onEsc);
    return () => window.removeEventListener("ems:escape", onEsc);
  }, [onClose]);

  const commands = useMemo<Cmd[]>(() => {
    // "Go" rows are DERIVED from the single nav tree so they match the tab
    // strip exactly (same labels, same chords) and can never drift. The
    // per-tab last-location memory makes a palette hop restore where the
    // operator left that tab (NAV-02). keywords carries the raw English label
    // + bare chord letter so cross-locale muscle-memory search still works.
    const list: Cmd[] = NAV_TREE.flatMap((m) =>
      m.tabs.map((tab) => ({
        id: `go-${tab.path}`,
        label: t(NAV_LABEL_KEY[tab.label] ?? tab.label),
        hint: NAV_CHORD[tab.path] ? `g ${NAV_CHORD[tab.path]}` : undefined,
        keywords: `${tab.label} ${NAV_CHORD[tab.path] ?? ""}`,
        group: "Go" as const,
        icon: ArrowRight,
        run: () => navigate(rememberedTabLocation(tab.path) ?? tab.path),
      })),
    );
    // Alarm rules was demoted from a Manage tab (F01) — it's reached via the
    // Alarms hub's Live|Rules switch — but the palette keeps a direct hop to
    // the rule editor so the deep route stays one keystroke away.
    list.push({
      id: "go-alarm-rules",
      label: t(NAV_LABEL_KEY["Alarm rules"]),
      keywords: "Alarm rules",
      group: "Go", icon: ArrowRight,
      run: () => navigate(rememberedTabLocation("/alarms/rules") ?? "/alarms/rules"),
    });
    // Site swaps: mock mode offers the synthetic TOPOLOGIES; live mode
    // offers ONLY the adapter's real availableSites (the mock topologies
    // reuse real site names but silently no-op against the live store).
    // The whole group is omitted when there is nothing to swap to.
    if (mode === "mock") {
      for (const t of TOPOLOGIES) {
        list.push({
          id: `site-${t.id}`, label: `Switch to ${t.name}`,
          hint: `${t.bess.length}× BESS · ${t.smartmeters.some((m) => m.role === "pv") ? "with PV" : "no PV"}`,
          group: "Site", icon: Building2, run: () => setSite(t.id),
        });
      }
    } else if (availableSites.length > 1) {
      for (const s of availableSites) {
        list.push({
          id: `site-${s.id}`, label: `Switch to ${s.name}`,
          group: "Site", icon: Building2, run: () => setSite(s.id),
        });
      }
    }
    for (const b of topology.bess) {
      list.push({
        id: `bess-${b.id}`, label: `Inspect ${b.name}`,
        hint: `${b.family} · ${b.protocol}`,
        group: "Go", icon: Battery, run: () => navigate(`/bess/${b.id}`),
      });
    }
    // Active alarms — the entities operators most need to jump to during an
    // incident. Each entry deep-links to its auto-expanded row on /alarms.
    for (const a of (alarms ?? []).filter((x) => x.status === "active")) {
      list.push({
        id: `alarm-${a.id}`,
        label: `Alarm: ${a.message} · ${a.device_name} · ${a.severity}`,
        group: "Alarm", icon: Bell,
        run: () => navigate("/alarms", { id: a.id }),
      });
    }
    // Every override is a live plant write (N13). OFF is the worst —
    // destructive tint + critical badge; the three operational modes carry
    // the calmer 'write' marker (amber badge, uncoloured row text) so OFF
    // still reads as the worst row.
    const overrides: { mode: ControllerMode; label: string; warn?: boolean }[] = [
      { mode: "PeakShaving", label: "Override → Peak Shaving (30 min)" },
      { mode: "gridZeroControl", label: "Override → Grid Zero (30 min)" },
      { mode: "ConstantPower", label: "Override → Constant Power (30 min)" },
      { mode: "OFF", label: "Override → OFF (30 min)", warn: true },
    ];
    for (const o of overrides) {
      list.push({
        id: `override-${o.mode}`, label: o.label, group: "Mode", icon: Cog,
        writeRisk: o.warn ? "destructive" : "write",
        confirm: `Press Enter again to apply ${o.mode} for 30 min`,
        run: async () => {
          await adapter.setOverride({ mode: o.mode, expires_at: Date.now() + 30 * 60_000 });
          pushToast({ level: o.warn ? "warn" : "ok", title: `Override applied`, detail: `${o.mode} for 30 min by ${operator}` });
        },
      });
    }
    list.push({
      id: "ack-all-warn", label: "Acknowledge all warnings", group: "Action", icon: Bell,
      hint: "minor + warning severities only",
      // Bulk state write — carries the 'write' marker (N13).
      writeRisk: "write",
      confirm: "Press Enter again to acknowledge all warnings",
      run: async () => {
        const list = await adapter.alarms();
        const targets = list.filter((a) => a.status === "active" && (a.severity === "warning" || a.severity === "minor"));
        await Promise.all(targets.map((a) => adapter.ackAlarm(a.id, operator)));
        pushToast({ level: "ok", title: `Acknowledged ${targets.length} alarm${targets.length === 1 ? "" : "s"}`, detail: `By ${operator}` });
      },
    });
    list.push({
      id: "clear-override", label: "Clear active override", group: "Action", icon: Cog,
      // Removing the override resumes the schedule — a live plant write the
      // finding omitted; mark it too so the law stays consistent (N13).
      writeRisk: "write",
      confirm: "Press Enter again to clear the active override",
      run: async () => {
        await adapter.setOverride(null);
        pushToast({ level: "ok", title: "Override cleared", detail: "Schedule resumes" });
      },
    });
    list.push({
      id: "set-operator", label: "Set operator name…", group: "Action", icon: User,
      hint: `audit trail · now: ${operator}`,
      run: () => {
        // window.prompt matches the app's minimal-dialog reality — there is
        // no text-input modal primitive yet (DATA-10). A sandboxed iframe
        // may return null from prompt(); treated as cancel.
        const name = window.prompt("Operator name (recorded in the alarm audit trail):", operator);
        const next = name?.trim();
        if (next && next !== operator) {
          setOperator(next);
          pushToast({ level: "ok", title: `Operator set to ${next}`, detail: "Used for alarm acks, comments and overrides" });
        }
      },
    });
    // Per-site personal "home view" (F23): set the current page as the route
    // a fresh "/" load lands on (after return-to-where-you-were and admin
    // ?mode=). A preference, not a plant change — write/Action lane, NO
    // "changes the plant" badge. Two-step confirm to set; single-step clear.
    if (isValidHomeView(pathname)) {
      list.push({
        id: "set-home-view", label: "Set home view to this page", group: "Action", icon: ArrowRight,
        hint: currentHome === pathname ? "already your home view" : `opens here on a fresh load · now: ${currentHome ?? "Overview (default)"}`,
        confirm: "Press Enter again to set this page as your home view",
        run: () => {
          setPref(topology.id, HOME_VIEW_PREF, pathname);
          pushToast({ level: "ok", title: "Home view set", detail: `Fresh loads of this site now open on ${pathname}` });
        },
      });
    }
    if (currentHome) {
      list.push({
        id: "clear-home-view", label: "Clear home view", group: "Action", icon: ArrowRight,
        hint: `back to Overview · now: ${currentHome}`,
        run: () => {
          setPref(topology.id, HOME_VIEW_PREF, "");
          pushToast({ level: "ok", title: "Home view cleared", detail: "Fresh loads return to Overview" });
        },
      });
    }
    list.push({
      id: "glossary", label: "Legend & glossary", group: "Action", icon: Search,
      hint: "what every term, sign and color means",
      run: () => window.dispatchEvent(new CustomEvent("ems:glossary")),
    });
    return list;
  }, [adapter, alarms, availableSites, currentHome, mode, navigate, operator, pathname, setOperator, setPref, setSite, t, topology]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) =>
      c.label.toLowerCase().includes(q)
      || c.group.toLowerCase().includes(q)
      || (c.hint && c.hint.toLowerCase().includes(q))
      || (c.keywords && c.keywords.toLowerCase().includes(q))
    );
  }, [commands, query]);

  const grouped = useMemo(() => {
    const groups = new Map<string, Cmd[]>();
    for (const c of filtered) {
      const arr = groups.get(c.group) ?? [];
      arr.push(c);
      groups.set(c.group, arr);
    }
    // Fixed group order (Part 3): write/action lanes (Mode, Action) always
    // render LAST, below navigation — they can never sit above a "Go" row.
    return Array.from(groups.entries()).sort(
      ([a], [b]) => GROUP_ORDER[a as CmdGroup] - GROUP_ORDER[b as CmdGroup],
    );
  }, [filtered]);

  // Shared by Enter and click. Write commands carrying `confirm` are
  // two-step: the first activation arms (the row shows the confirm text in
  // warn tint), the second consecutive activation runs. Navigation stays
  // single-step.
  function execute(c: Cmd) {
    if (c.confirm && armedId !== c.id) {
      setArmedId(c.id);
      return;
    }
    // Async write commands (overrides, bulk ack) push their success toast
    // AFTER their awaits resolve — so a rejection must surface here, or a
    // failed setOverride (incl. OFF) silently looks applied (XTRA-01).
    Promise.resolve(c.run()).catch((err) =>
      pushToast({ level: "critical", title: "Command failed", detail: err instanceof Error ? err.message : String(err) }),
    );
    onClose();
  }

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setArmedId(null);
      setCursor((c) => Math.min(filtered.length - 1, c + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setArmedId(null);
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const sel = filtered[cursor];
      if (sel) execute(sel);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  }

  if (!open) return null;
  let runningIdx = -1;
  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center bg-bg/70 px-4 pt-[12vh] backdrop-blur-sm" onClick={onClose}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        // Initial focus = the search input (effect above); Esc is handled in
        // the input's handleKey + the window-level ems:escape listener; Tab
        // wraps inside the panel (XTRA-04 minimal semantics).
        onKeyDown={(e) => trapTabKey(e, panelRef.current)}
        className="w-full max-w-xl overflow-hidden rounded-xl border border-edge bg-surface shadow-2xl shadow-black/40"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-edge px-4 py-3">
          <Search size={14} className="text-text-muted" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setCursor(0); setArmedId(null); }}
            onKeyDown={handleKey}
            placeholder="Type a command — pages, sites, alarms, modes, actions…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-text-faint"
          />
          <span className="rounded border border-edge px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-text-faint">esc</span>
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {grouped.length === 0 && (
            <div className="px-4 py-8 text-center text-xs text-text-muted">No matches.</div>
          )}
          {grouped.map(([group, cmds]) => (
            <Fragment key={group}>
              <div className="border-y border-edge bg-surface-2/40 px-4 py-1.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-text-faint">
                {group}
              </div>
              {cmds.map((c) => {
                runningIdx += 1;
                const idx = runningIdx;
                const isCursor = idx === cursor;
                const Icon = c.icon;
                // N13: badge + tint key off the dedicated writeRisk field,
                // not an overloaded hint string. 'destructive' (OFF) is the
                // worst — red row text + critical badge; 'write' gets the
                // calmer amber badge with NO red row text so OFF reads worst.
                const dest = c.writeRisk === "destructive";
                const plantWrite = c.writeRisk === "write" || dest;
                const armed = armedId === c.id;
                return (
                  <button
                    key={c.id}
                    // onMouseMove, NOT onMouseEnter: only an actual mouse
                    // movement may steal the cursor — rows scrolling under a
                    // parked pointer while the user types must not hijack
                    // the selection (XTRA-01).
                    onMouseMove={() => {
                      if (cursor !== idx) {
                        setCursor(idx);
                        setArmedId(null);
                      }
                    }}
                    onClick={() => execute(c)}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition",
                      isCursor && !armed && "bg-accent/15",
                      armed && "bg-warn/15",
                      dest && "text-critical",
                    )}
                  >
                    <Icon size={14} className={cn(armed ? "text-warn" : isCursor ? "text-accent" : "text-text-muted")} />
                    <span className={cn("flex-1", armed && "font-medium text-warn")}>{armed ? c.confirm : c.label}</span>
                    {c.hint && !armed && (
                      <span className="rounded border border-edge px-1.5 py-0.5 font-mono text-[10px] text-text-muted">{c.hint}</span>
                    )}
                    {/* Always-visible "changes the plant" cue on EVERY plant
                        write (N13): a badge in the same slot as the chord
                        box, so every override/bulk write reads as plant-
                        altering before it is armed. Two-tier — OFF gets the
                        critical badge, operational writes the calmer warn
                        badge — so OFF still reads worst. */}
                    {plantWrite && !armed && (
                      <span
                        className={cn(
                          "rounded px-1.5 py-0.5 text-[10px] font-medium",
                          dest ? "bg-critical/15 text-critical" : "bg-warn/15 text-warn",
                        )}
                      >
                        {t("palette.changesPlant")}
                      </span>
                    )}
                  </button>
                );
              })}
            </Fragment>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-edge bg-surface-2 px-4 py-2 text-[10px] text-text-muted">
          <div className="flex items-center gap-3">
            <span><kbd className="rounded border border-edge px-1">↑</kbd> <kbd className="rounded border border-edge px-1">↓</kbd> navigate</span>
            <span><kbd className="rounded border border-edge px-1">↵</kbd> run</span>
            <span><kbd className="rounded border border-edge px-1">esc</kbd> close</span>
          </div>
          <span>operator: <strong className="text-text">{operator}</strong></span>
        </div>
      </div>
    </div>
  );
}
